
// components/LocationsCarousel.js
import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "../../context/LocationContext";
import "./LocationsCarousel.css";

const LocationsCarousel = () => {
  const { maharashtraLocations, currentLocation, changeLocation } =
    useLocation();
  const [activeLocation, setActiveLocation] = useState(currentLocation);
  const [isPaused, setIsPaused] = useState(false);
  const carouselRef = useRef(null);
  const animationRef = useRef(null);
  const scrollPositionRef = useRef(0);

  // Duplicate locations for infinite scroll effect
  const duplicatedLocations = [
    ...maharashtraLocations,
    ...maharashtraLocations,
    ...maharashtraLocations,
  ];

  useEffect(() => {
    // Auto-scroll animation
    const scrollCarousel = () => {
      if (carouselRef.current && !isPaused) {
        scrollPositionRef.current += 1;

        // Reset to beginning when reaching the duplicated section
        if (scrollPositionRef.current >= carouselRef.current.scrollWidth / 3) {
          scrollPositionRef.current = 0;
        }

        carouselRef.current.scrollLeft = scrollPositionRef.current;
      }
      animationRef.current = requestAnimationFrame(scrollCarousel);
    };

    animationRef.current = requestAnimationFrame(scrollCarousel);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPaused]);

  const handleLocationHover = (location) => {
    setActiveLocation(location.name);
    setIsPaused(true);
  };

  const handleLocationLeave = () => {
    setActiveLocation(currentLocation);
    setIsPaused(false);
  };

  const handleLocationClick = (location) => {
    changeLocation(location.name);
  };

  // Background gradient colors for different locations using the palette
  const getLocationColor = (name) => {
    const colors = {
      Amravati: "#447793", // Slate
      Akola: "#2D5A74",    // Midnight
      Nagpur: "#123249",   // Dark Navy
      Chandrapur: "#447793", // Slate
      Yavatmal: "#2D5A74",   // Midnight
      Gondia: "#123249",     // Dark Navy
      Gadchiroli: "#447793", // Slate
      Wardha: "#2D5A74",     // Midnight
      Bhandara: "#123249",   // Dark Navy
      Buldhana: "#447793",   // Slate
      Washim: "#2D5A74",     // Midnight
    };
    return colors[name] || "#447793";
  };

  return (
    <div className="locations-carousel-section">
      <div className="container">
        <div className="section-header">
          <h2 className="section-title">Our Presence in Maharashtra</h2>
          <p className="section-subtitle">
            Scroll through our service locations across Maharashtra
          </p>
        </div>

        {/* Location Cards Carousel */}
        <div
          className="carousel-container"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <div className="locations-carousel" ref={carouselRef}>
            {duplicatedLocations.map((location, index) => (
              <div
                key={`${location.id}-${index}`}
                className={`location-card ${
                  activeLocation === location.name ? "active" : ""
                }`}
                style={{
                  "--location-color": getLocationColor(location.name),
                  "--hover-color": `linear-gradient(135deg, #447793, #2D5A74)`,
                }}
                onMouseEnter={() => handleLocationHover(location)}
                onMouseLeave={handleLocationLeave}
                onClick={() => handleLocationClick(location)}
              >
                <div className="location-card-inner">
                  <div className="location-number">
                    {(index % maharashtraLocations.length) + 1}
                  </div>
                  <h3 className="location-name">{location.name}</h3>
                  <div className="location-address">
                    <span className="address-icon">📍</span>
                    {location.address}
                  </div>

                  {/* Animated background elements */}
                  <div className="location-bg-elements">
                    <div className="bg-circle-1"></div>
                    <div className="bg-circle-2"></div>
                    <div className="bg-circle-3"></div>
                  </div>

                  {/* Hover Overlay */}
                  <div className="location-overlay">
                    <div className="overlay-content">
                      <h4>Solar Projects in {location.name}</h4>
                      <p>
                        Multiple residential and commercial solar installations
                      </p>
                      <div className="location-stats">
                        <div className="stat">
                          <div className="stat-value">50+</div>
                          <div className="stat-label">Projects</div>
                        </div>
                        <div className="stat">
                          <div className="stat-value">10MW+</div>
                          <div className="stat-label">Capacity</div>
                        </div>
                      </div>
                      <button className="view-projects-btn">
                        View Projects →
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Scroll Indicators */}
          <div className="scroll-indicators">
            <div className={`indicator ${!isPaused ? "active" : ""}`}></div>
            <div className={`indicator ${isPaused ? "active" : ""}`}></div>
          </div>

          {/* Current Location Display */}
          <div className="current-location-display">
            <div className="current-location-badge">
              <span className="current-icon">📍</span>
              <span className="current-text">Currently Viewing: </span>
              <span className="current-name">{activeLocation}</span>
            </div>
          </div>
        </div>

        {/* Location Info Panel */}
        <div className="location-info-panel">
          <div className="info-content">
            <h3>Maharashtra Coverage</h3>
            <p>
              We provide comprehensive solar solutions across{" "}
              {maharashtraLocations.length} major districts in Maharashtra,
              ensuring clean energy access to both urban and rural areas.
            </p>

            <div className="coverage-stats">
              <div className="coverage-stat">
                <div className="coverage-number">
                  {maharashtraLocations.length}
                </div>
                <div className="coverage-label">Districts Covered</div>
              </div>
              <div className="coverage-stat">
                <div className="coverage-number">5000+</div>
                <div className="coverage-label">Installations</div>
              </div>
              <div className="coverage-stat">
                <div className="coverage-number">24/7</div>
                <div className="coverage-label">Support Available</div>
              </div>
            </div>
          </div>

          <div className="info-map">
            <div className="map-placeholder">
              <div className="map-dots">
                {maharashtraLocations.map((loc, index) => (
                  <div
                    key={loc.id}
                    className={`map-dot ${
                      activeLocation === loc.name ? "active" : ""
                    }`}
                    style={{
                      top: `${20 + Math.sin(index) * 30}%`,
                      left: `${30 + Math.cos(index) * 40}%`,
                      "--dot-color": getLocationColor(loc.name),
                    }}
                    onMouseEnter={() => handleLocationHover(loc)}
                    onMouseLeave={handleLocationLeave}
                  >
                    <div className="dot-label">{loc.name}</div>
                  </div>
                ))}
              </div>
              <div className="map-title">
                <span>Maharashtra Map</span>
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d35602.40292825899!2d79.14732526803402!3d21.104499928073757!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bd4b970c1612117%3A0x217cca3b13c6dbb7!2sBlue%20sky%20energy!5e0!3m2!1sen!2sin!4v1766398621132!5m2!1sen!2sin"
                  width="100%"
                  height="300"
                  style={{ border: 0, borderRadius: '12px' }}
                  allowFullScreen={true}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Maharashtra Map"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationsCarousel;